import { motion } from "framer-motion";
import {
  Monitor,
  Video,
  Target,
  Ban,
  Compass,
  ArrowRight,
} from "lucide-react";
import SplitReveal from "@/components/anim/SplitReveal";

/**
 * Challenges Sektion. 5 Painpoints, die direkt auf CRE8's 3 Kern
 * Services zugeschnitten sind (Websites, Content, Werbeanzeigen) plus
 * zwei übergeordnete Probleme (unzuverlässige Agenturen, fehlende
 * Strategie). Ziel: Interessent liest, identifiziert sich, klickt den
 * CTA unter den Karten.
 */

const CHALLENGES = [
  {
    icon: Monitor,
    title: "Deine Website bringt keine Anfragen",
    text:
      "Sie lädt langsam, wirkt veraltet oder ist unklar strukturiert. Interessenten springen ab statt sich zu melden.",
  },
  {
    icon: Video,
    title: "Guter Content und Creatives fehlen",
    text:
      "Ohne professionelle Reels, Fotos und Videos passiert nichts. Deine Zielgruppe scrollt an dir vorbei.",
  },
  {
    icon: Target,
    title: "Werbeanzeigen verbrennen Budget",
    text:
      "Meta und Google Ads laufen, aber du weißt nicht ob sie wirklich Kunden bringen. Tracking und Attribution sind Blackbox.",
  },
  {
    icon: Ban,
    title: "Agenturen oder Freelancer liefern nicht",
    text:
      "Viel versprochen, schlecht geliefert. Ergebnisse bleiben aus, Budget verbrannt, Vertrauen weg.",
  },
  {
    icon: Compass,
    title: "Kein roter Faden im Marketing",
    text:
      "Website, Content und Ads laufen unabhängig voneinander. Nichts baut aufeinander auf, niemand denkt strategisch mit.",
  },
];

// 6-Spalten Raster: 3 Karten oben, 2 Karten unten mittig, asymmetrisch
// und lesbar auf großen Screens.
const COL_CLASSES = [
  "lg:col-span-2 lg:col-start-1",
  "lg:col-span-2 lg:col-start-3",
  "lg:col-span-2 lg:col-start-5",
  "lg:col-span-2 lg:col-start-2",
  "lg:col-span-2 lg:col-start-4",
];

export default function Challenges() {
  return (
    <section
      id="challenges"
      data-testid="challenges-section"
      aria-labelledby="challenges-heading"
      className="relative bg-ink py-16 lg:py-24 overflow-hidden border-t border-white/5"
    >
      {/* Ambient warm gradient, signal that we're talking about pain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(800px circle at 50% 0%, rgba(229,164,74,0.06), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 relative">
        {/* Header */}
        <div className="max-w-5xl">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bone/60 mb-6"
          >
            <span
              className="h-px w-10"
              style={{ background: "#E5A44A" }}
            />
            Herausforderungen
          </motion.div>

          <SplitReveal
            as="h2"
            id="challenges-heading"
            lines={[
              "Bevor Wachstum planbar wird,",
              "scheitern die meisten hier…",
            ]}
            className="font-display font-black tracking-tighter text-bone leading-[1.0]"
            duration={0.9}
            stagger={0.05}
          />
          <style>{`#challenges h2 { font-size: clamp(1.5rem, 3.4vw, 2.75rem); }`}</style>
        </div>

        {/* Grid mit 5 Engpass Karten */}
        <div className="mt-14 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-6">
          {CHALLENGES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                data-testid={`challenge-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{
                  duration: 0.7,
                  delay: 0.1 + i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4 }}
                className={`group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 lg:p-7 hover:border-white/20 hover:bg-white/[0.04] transition-[background-color,border-color] duration-500 ${COL_CLASSES[i]}`}
              >
                {/* Nummer (top right) */}
                <div className="absolute top-4 right-5 text-[10px] font-mono uppercase tracking-widest text-bone/25 tabular-nums">
                  0{i + 1}
                </div>

                {/* Icon in warm amber */}
                <div
                  className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-5 transition-colors"
                  style={{
                    background: "rgba(229,164,74,0.08)",
                    border: "1px solid rgba(229,164,74,0.25)",
                    color: "#E5A44A",
                  }}
                >
                  <Icon size={20} strokeWidth={1.8} />
                </div>

                <div className="text-base lg:text-lg font-display font-bold text-bone mb-2 leading-tight">
                  {c.title}
                </div>
                <p className="text-sm text-bone/60 leading-relaxed">
                  {c.text}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA unter den Karten. User hat die Painpoints gelesen, jetzt Lösung */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-12 lg:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <a
            href="#contact"
            data-testid="challenges-cta"
            className="btn-cyan-ghost inline-flex items-center gap-3 px-7 py-4 text-sm font-semibold rounded-full"
          >
            Jetzt Engpässe lösen
            <ArrowRight size={14} />
          </a>
          <span className="text-xs text-bone/45 max-w-md leading-relaxed">
            Kostenfreies Erstgespräch. Kein Verkaufsgespräch, nur eine
            ehrliche Einschätzung ob und wie wir helfen können.
          </span>
        </motion.div>
      </div>
    </section>
  );
}
